// src/components/BatchJobCreator.jsx
import { useState } from 'react';
import { useGraphQL } from '../graphql';
import Modal from './Modal';
import {
    CheckIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';

// TED Product Classification categories
const PC_CATEGORIES = {
    'agfo': { label: 'Agriculture & Food', color: '#22C55E' },
    'comp': { label: 'Computer & IT Services', color: '#3B82F6' },
    'core': { label: 'Construction & Real Estate', color: '#F97316' },
    'dese': { label: 'Defence & Security', color: '#EF4444' },
    'educ': { label: 'Education', color: '#A855F7' },
    'ener': { label: 'Energy', color: '#EAB308' },
    'ensa': { label: 'Environment & Sanitation', color: '#14B8A6' },
    'fina': { label: 'Finance', color: '#6366F1' },
    'mapr': { label: 'Materials & Products', color: '#78716C' },
    'mior': { label: 'Mining & Ores', color: '#71717A' },
    'serv': { label: 'Other Services', color: '#EC4899' },
    'prpu': { label: 'Printing & Publishing', color: '#8B5CF6' },
    'reco': { label: 'Research & Development', color: '#06B6D4' },
    'teeq': { label: 'Technology & Equipment', color: '#10B981' },
    'tran': { label: 'Transport', color: '#F59E0B' }
};

const BatchJobCreator = ({ isOpen, onClose, onCreated }) => {
    const { executeMutation, mutations } = useGraphQL();

    // Selected categories
    const [selectedCategories, setSelectedCategories] = useState(new Set(['comp', 'teeq', 'fina']));

    // Schedule config
    const [startHour, setStartHour] = useState(6);
    const [intervalMinutes, setIntervalMinutes] = useState(30);
    const [frequency, setFrequency] = useState('daily'); // 'daily' | 'hourly' | 'weekly'
    const [weekDay, setWeekDay] = useState(1); // Monday

    // Job parameters
    const [country, setCountry] = useState('DEU');
    const [language, setLanguage] = useState('DEU');
    const [daysBack, setDaysBack] = useState(1);
    const [daysAhead, setDaysAhead] = useState(90);
    const [saveTenders, setSaveTenders] = useState(true);
    const [deepCheck, setDeepCheck] = useState(true);
    const [limit, setLimit] = useState(250);

    // Progress
    const [, setCreating] = useState(false);
    const [progress, setProgress] = useState([]);
    const [step, setStep] = useState('config'); // 'config' | 'preview' | 'creating' | 'done'

    const toggleCategory = (cat) => {
        const newSet = new Set(selectedCategories);
        if (newSet.has(cat)) {
            newSet.delete(cat);
        } else {
            newSet.add(cat);
        }
        setSelectedCategories(newSet);
    };

    const selectAll = () => {
        setSelectedCategories(new Set(Object.keys(PC_CATEGORIES)));
    };

    const selectNone = () => {
        setSelectedCategories(new Set());
    };

    // Generate cron expression for a job
    const generateCron = (index) => {
        const totalMinutes = startHour * 60 + (index * intervalMinutes);
        const hour = Math.floor(totalMinutes / 60) % 24;
        const minute = totalMinutes % 60;

        switch (frequency) {
            case 'hourly':
                return `${minute} * * * *`;
            case 'weekly':
                return `${minute} ${hour} * * ${weekDay}`;
            case 'daily':
            default:
                return `${minute} ${hour} * * *`;
        }
    };

    // Build query for a category
    const buildQuery = (category) => {
        return `(total-value = (1 <> 9999999999999)) AND (buyer-country IN (${country})) AND (submission-language IN (${language})) AND (procedure-type IN (oth-mult innovation A comp-dial F neg-w-call comp-tend 4 6 8 9 Z V restricted oth-single exp-int-rail open)) AND (total-value-cur IN (EUR)) AND (contract-nature IN (works 9 Z supplies combined services)) AND PC=(${category}) AND (notice-type IN (cn-standard cn-social pin-cfc-standard pin-cfc-social pin-rtl))`;
    };

    // Build job parameters
    const buildParameters = (category) => {
        return {
            saveTenders,
            deepCheck,
            source: 'TED',
            query: buildQuery(category),
            daysBack,
            daysAhead,
            limit,
            maxResults: 0
        };
    };

    // Preview data
    const previewJobs = Array.from(selectedCategories).map((cat, index) => ({
        category: cat,
        label: PC_CATEGORIES[cat].label,
        color: PC_CATEGORIES[cat].color,
        cron: generateCron(index),
        parameters: buildParameters(cat)
    }));

    // Create all jobs
    const createJobs = async () => {
        setStep('creating');
        setCreating(true);
        setProgress([]);

        const results = [];

        for (let i = 0; i < previewJobs.length; i++) {
            const job = previewJobs[i];

            try {
                const variables = {
                    input: {
                        source: job.parameters.source,
                        query: job.parameters.query,
                        daysBack: job.parameters.daysBack,
                        daysAhead: job.parameters.daysAhead,
                        limit: job.parameters.limit,
                        maxResults: job.parameters.maxResults,
                        saveTenders: job.parameters.saveTenders,
                        deepCheck: job.parameters.deepCheck,
                        batchSize: job.parameters.batchSize || null,
                        cronSchedule: job.cron
                    }
                };

                const result = await executeMutation(mutations.adminCheckNewTenders, variables);

                results.push({
                    category: job.category,
                    label: job.label,
                    success: result.adminCheckNewTenders.success,
                    jobId: result.adminCheckNewTenders.jobId,
                    error: result.adminCheckNewTenders.error
                });

                setProgress([...results]);

                // Small delay between requests
                await new Promise(resolve => setTimeout(resolve, 200));

            } catch (err) {
                results.push({
                    category: job.category,
                    label: job.label,
                    success: false,
                    error: err.message
                });
                setProgress([...results]);
            }
        }

        setCreating(false);
        setStep('done');
    };

    const handleClose = () => {
        if (step === 'done') {
            onCreated?.();
        }
        setStep('config');
        setProgress([]);
        onClose();
    };

    const formatTime = (cronExpr) => {
        const parts = cronExpr.split(' ');
        if (parts[1] === '*') {
            return `Every hour at :${parts[0].padStart(2, '0')}`;
        }
        return `${parts[1].padStart(2, '0')}:${parts[0].padStart(2, '0')}`;
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Batch Create TED Crawler Jobs" size="lg">
            <div className="space-y-6">

                {/* Step: Config */}
                {step === 'config' && (
                    <>
                        {/* Category Selection */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-gray-300">
                                    Select Categories ({selectedCategories.size} selected)
                                </label>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={selectAll}
                                        className="text-xs text-blue-700 hover:text-blue-700"
                                    >
                                        Select All
                                    </button>
                                    <button
                                        type="button"
                                        onClick={selectNone}
                                        className="text-xs text-gray-400 hover:text-gray-300"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                {Object.entries(PC_CATEGORIES).map(([key, { label, color }]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => toggleCategory(key)}
                                        className={`p-2 border-2 text-left text-sm transition-all ${
 selectedCategories.has(key)
 ? 'border-primary bg-primary/20'
 : 'border-gray-600 bg-gray-700 hover:border-gray-500'
 }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <div
                                                className="w-3 h-3 flex-shrink-0"
                                                style={{ backgroundColor: color }}
                                            />
                                            <span className="text-white truncate">{label}</span>
                                        </div>
                                        <span className="text-xs text-gray-400 uppercase">{key}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Schedule Settings */}
                        <div className="border-t border-gray-700 pt-4">
                            <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                                <ClockIcon className="w-4 h-4" />
                                Schedule Settings
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Frequency</label>
                                    <select
                                        value={frequency}
                                        onChange={(e) => setFrequency(e.target.value)}
                                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                    >
                                        <option value="daily">Daily</option>
                                        <option value="hourly">Every Hour</option>
                                        <option value="weekly">Weekly</option>
                                    </select>
                                </div>
                                {frequency === 'weekly' && (
                                    <div>
                                        <label className="block text-xs text-gray-400 mb-1">Day of Week</label>
                                        <select
                                            value={weekDay}
                                            onChange={(e) => setWeekDay(parseInt(e.target.value))}
                                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                        >
                                            <option value={0}>Sunday</option>
                                            <option value={1}>Monday</option>
                                            <option value={2}>Tuesday</option>
                                            <option value={3}>Wednesday</option>
                                            <option value={4}>Thursday</option>
                                            <option value={5}>Friday</option>
                                            <option value={6}>Saturday</option>
                                        </select>
                                    </div>
                                )}
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Start Hour (UTC)</label>
                                    <select
                                        value={startHour}
                                        onChange={(e) => setStartHour(parseInt(e.target.value))}
                                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                    >
                                        {Array.from({ length: 24 }, (_, i) => (
                                            <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Interval Between Jobs</label>
                                    <select
                                        value={intervalMinutes}
                                        onChange={(e) => setIntervalMinutes(parseInt(e.target.value))}
                                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                    >
                                        <option value={10}>10 minutes</option>
                                        <option value={15}>15 minutes</option>
                                        <option value={20}>20 minutes</option>
                                        <option value={30}>30 minutes</option>
                                        <option value={45}>45 minutes</option>
                                        <option value={60}>1 hour</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Query Parameters */}
                        <div className="border-t border-gray-700 pt-4">
                            <h4 className="text-sm font-medium text-gray-300 mb-3">Query Parameters</h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Country</label>
                                    <select
                                        value={country}
                                        onChange={(e) => setCountry(e.target.value)}
                                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                    >
                                        <option value="DEU">🇩🇪 Germany</option>
                                        <option value="AUT">🇦🇹 Austria</option>
                                        <option value="CHE">🇨🇭 Switzerland</option>
                                        <option value="FRA">🇫🇷 France</option>
                                        <option value="NLD">🇳🇱 Netherlands</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Language</label>
                                    <select
                                        value={language}
                                        onChange={(e) => setLanguage(e.target.value)}
                                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                    >
                                        <option value="DEU">German</option>
                                        <option value="ENG">English</option>
                                        <option value="FRA">French</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Days Back (Published)</label>
                                    <input
                                        type="number"
                                        value={daysBack}
                                        onChange={(e) => setDaysBack(parseInt(e.target.value) || 0)}
                                        min={0}
                                        max={365}
                                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Days Ahead (Deadline)</label>
                                    <input
                                        type="number"
                                        value={daysAhead}
                                        onChange={(e) => setDaysAhead(parseInt(e.target.value) || 0)}
                                        min={0}
                                        max={365}
                                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Limit per Request</label>
                                    <input
                                        type="number"
                                        value={limit}
                                        onChange={(e) => setLimit(parseInt(e.target.value) || 50)}
                                        min={10}
                                        max={250}
                                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-4 mt-3">
                                <label className="flex items-center gap-2 text-sm text-gray-300">
                                    <input
                                        type="checkbox"
                                        checked={saveTenders}
                                        onChange={(e) => setSaveTenders(e.target.checked)}
                                        className="border-gray-600 bg-gray-700 text-primary"
                                    />
                                    Save Tenders
                                </label>
                                <label className="flex items-center gap-2 text-sm text-gray-300">
                                    <input
                                        type="checkbox"
                                        checked={deepCheck}
                                        onChange={(e) => setDeepCheck(e.target.checked)}
                                        className="border-gray-600 bg-gray-700 text-primary"
                                    />
                                    Deep Check (Download docs)
                                </label>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-between pt-4 border-t border-gray-700">
                            <button
                                onClick={handleClose}
                                className="px-4 py-2 font-mono text-xs font-bold uppercase bg-gray-800 text-gray-400 border border-gray-700 hover:bg-gray-700 hover:text-white"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => setStep('preview')}
                                disabled={selectedCategories.size === 0}
                                className="px-4 py-2 font-mono text-xs font-bold uppercase bg-primary text-on-accent hover:brightness-110 disabled:opacity-50"
                            >
                                Preview Jobs ({selectedCategories.size})
                            </button>
                        </div>
                    </>
                )}

                {/* Step: Preview */}
                {step === 'preview' && (
                    <>
                        <div className="bg-blue-50 border border-blue-200 p-3 flex items-start gap-2">
                            <InformationCircleIcon className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-700">
                                <p className="font-medium">Ready to create {previewJobs.length} scheduled jobs</p>
                                <p className="text-blue-700">Jobs will be staggered starting at {String(startHour).padStart(2, '0')}:00 with {intervalMinutes} minute intervals.</p>
                            </div>
                        </div>

                        <div className="max-h-96 overflow-y-auto space-y-2">
                            {previewJobs.map((job, index) => (
                                <div
                                    key={job.category}
                                    className="bg-gray-700 border border-gray-600 p-3 flex items-center gap-3"
                                >
                                    <div
                                        className="w-3 h-3 flex-shrink-0"
                                        style={{ backgroundColor: job.color }}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-white">{job.label}</span>
                                            <span className="text-xs text-gray-400 uppercase">({job.category})</span>
                                        </div>
                                        <div className="text-xs text-gray-400 font-mono mt-1">
                                            {job.cron} → {formatTime(job.cron)}
                                        </div>
                                    </div>
                                    <span className="text-sm text-gray-400">#{index + 1}</span>
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-between pt-4 border-t border-gray-700">
                            <button
                                onClick={() => setStep('config')}
                                className="px-4 py-2 font-mono text-xs font-bold uppercase bg-gray-800 text-gray-400 border border-gray-700 hover:bg-gray-700 hover:text-white"
                            >
                                Back
                            </button>
                            <button
                                onClick={createJobs}
                                className="px-4 py-2 font-mono text-xs font-bold uppercase bg-primary text-on-accent hover:brightness-110"
                            >
                                Create All Jobs
                            </button>
                        </div>
                    </>
                )}

                {/* Step: Creating */}
                {step === 'creating' && (
                    <>
                        <div className="text-center py-4">
                            <div className="animate-spin h-6 w-6 border-2 border-gray-600 border-t-white mx-auto mb-3"></div>
                            <p className="text-gray-300">Creating jobs... ({progress.length}/{previewJobs.length})</p>
                        </div>

                        <div className="max-h-64 overflow-y-auto space-y-1">
                            {progress.map((result, index) => (
                                <div
                                    key={index}
                                    className={`flex items-center gap-2 p-2 ${
 result.success ? 'bg-green-50' : 'bg-red-50'
 }`}
                                >
                                    {result.success ? (
                                        <CheckIcon className="w-4 h-4 text-green-700" />
                                    ) : (
                                        <XMarkIcon className="w-4 h-4 text-red-700" />
                                    )}
                                    <span className="text-sm text-white">{result.label}</span>
                                    {result.error && (
                                        <span className="text-xs text-red-700 ml-auto">{result.error}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {/* Step: Done */}
                {step === 'done' && (
                    <>
                        <div className="text-center py-6">
                            <CheckIcon className="w-12 h-12 text-green-700 mx-auto mb-3" />
                            <h3 className="text-lg font-medium text-white mb-2">Batch Creation Complete</h3>
                            <p className="text-gray-400">
                                Created {progress.filter(r => r.success).length} of {progress.length} jobs
                            </p>
                        </div>

                        {progress.some(r => !r.success) && (
                            <div className="bg-yellow-50 border border-yellow-200 p-3">
                                <div className="flex items-start gap-2">
                                    <ExclamationTriangleIcon className="w-5 h-5 text-yellow-700 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm font-medium text-yellow-700">Some jobs failed</p>
                                        <ul className="text-xs text-yellow-700 mt-1">
                                            {progress.filter(r => !r.success).map((r, i) => (
                                                <li key={i}>{r.label}: {r.error}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="max-h-48 overflow-y-auto space-y-1">
                            {progress.map((result, index) => (
                                <div
                                    key={index}
                                    className={`flex items-center gap-2 p-2 ${
 result.success ? 'bg-green-50' : 'bg-red-50'
 }`}
                                >
                                    {result.success ? (
                                        <CheckIcon className="w-4 h-4 text-green-700" />
                                    ) : (
                                        <XMarkIcon className="w-4 h-4 text-red-700" />
                                    )}
                                    <span className="text-sm text-white">{result.label}</span>
                                    {result.success && result.jobId && (
                                        <span className="text-xs text-gray-400 font-mono ml-auto">
                                            {result.jobId.slice(0, 8)}...
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-end pt-4 border-t border-gray-700">
                            <button
                                onClick={handleClose}
                                className="px-4 py-2 font-mono text-xs font-bold uppercase bg-primary text-on-accent hover:brightness-110"
                            >
                                Done
                            </button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default BatchJobCreator;